import type { Dimension } from '@/Enums/ReportBuilder/Dimension.enum';
import { CustomReportChannel } from '@/Enums/ReportBuilder/ReportChannel.enum';
import { RequestAbortSignal } from '@/Enums/ReportBuilder/RequestAbortSignal.enum';
import { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import { IFilterFormData } from '@/Interfaces/ReportBuilder/FilterFormData.interface';
import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import { ICustomReportTemplate } from '@/Interfaces/ReportBuilder/Template.interface';
import ShareModal from '@/components/Modal/ShareModal';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import { METRICS_OPTIONS_MAP, getDimensionsOptionsMap } from '@/constants/reportBuilder/omniPlatform';
import { parsedAdvertisersMapper } from '@/helpers/advertisers/advertiserMappers.helpers';
import {
  getDefaultFilters,
  getOverlappingColumns,
  getReportGenerationErrorMessage,
  hydrateTemplate,
  toScheduleFormDefaults,
} from '@/helpers/reportBuilder';
import { IRequestSaverFunctions } from '@/hooks/RequestSaver';
import {
  cancelScheduledReport,
  createTemplate,
  downloadReport,
  generateReport,
  scheduleReport,
  updateScheduledReport,
} from '@/store/api/reportBuilder.api';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import {
  selectOmniplatformCampaigns,
  selectOmniplatformEvents,
  selectOmniplatformGeneratedReports,
  selectOmniplatformScheduledReports,
  selectOmniplatformTemplates,
} from '@/store/selectors/reportBuilder/omniplatform.selectors';
import { selectSharedApiClients } from '@/store/selectors/sharedApi.selectors';
import {
  appendTemplate,
  getCampaigns,
  getEvents,
  getGeneratedReports,
  getScheduledReports,
  getTemplates,
  loadTemplate,
} from '@/store/slices/reportBuilder/omniplatformSlice';
import { ScheduledReportFilterDate } from '@/types/ReportBuilder/FilterDate.types';
import { CustomReportDownloadFunction } from '@/types/ReportBuilder/GridFunction.types';
import { SharesTargets } from '@/types/shares.types';
import { Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import SaveAsTemplateModal from './Components/SaveAsTemplateModal';
import ScheduleReportModal from './Components/ScheduleReportModal';
import Filter from './Filter';
import GeneratedReportsGrid from './GeneratedReportsGrid';
import { useModal } from './Hooks/useModal';
import { useScheduledReportEditing } from './Hooks/useScheduledReportEditing';
import { useTemplateReports } from './Hooks/useTemplateReports';
import ReportPreviewGrid from './ReportPreviewGrid';
import ScheduledReportsGrid from './ScheduledReportsGrid';
import TemplateReportsGrid from './TemplateReportsGrid';

interface IProps {
  openNoticeModal: () => void;
  requestSaver: IRequestSaverFunctions;
}

const CUSTOM_REPORT_CHANNEL = CustomReportChannel.Omniplatform;

const OmniplatformReportBuilder = ({ openNoticeModal, requestSaver }: IProps) => {
  //#region hooks
  const dispatch = useAppDispatch();
  const filterFormMethods = useForm<IFilterFormData>({
    defaultValues: getDefaultFilters(),
  });
  const { getValues, setValue, control } = filterFormMethods;
  //#endregion

  //#region state
  const { clients, clientsLoading } = useAppSelector(state => selectSharedApiClients(state, parsedAdvertisersMapper));
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const { templates, loadingTemplates } = useAppSelector(selectOmniplatformTemplates);
  const { campaigns, loadingCampaigns } = useAppSelector(selectOmniplatformCampaigns);
  const { events, loadingEvents } = useAppSelector(selectOmniplatformEvents);
  const { generatedReports, loadingGeneratedReports, generatedReportsTotal } = useAppSelector(
    selectOmniplatformGeneratedReports,
  );
  const { scheduledReports, loadingScheduledReports, scheduledReportsTotal } = useAppSelector(
    selectOmniplatformScheduledReports,
  );

  // Read at template-load time, so the callback below stays out of the options memo's ordering.
  const visibleDimensionValues = useRef<Dimension[]>([]);

  const scheduleModalState = useModal();
  const templateModalState = useModal();
  const scheduledReportEditingState = useScheduledReportEditing();
  const templateReports = useTemplateReports(CUSTOM_REPORT_CHANNEL);

  const [loadingInstantReportRun, setLoadingInstantReportRun] = useState<boolean>(false);
  //#endregion

  //#region useCallback
  const loadCampaigns = useCallback(
    (filterClients: IClientControlOption[], filterAdvertisers: IControlOption[]) => {
      dispatch(
        getCampaigns({
          clients: filterClients,
          advertisers: filterAdvertisers,
          platforms: getValues('platforms'),
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.Campaigns),
        }),
      );
    },
    [dispatch, getValues],
  );

  const loadEvents = useCallback(
    (filterClients: IClientControlOption[], filterAdvertisers: IControlOption[]) => {
      dispatch(
        getEvents({
          clients: filterClients,
          advertisers: filterAdvertisers,
          platforms: getValues('platforms'),
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.Events),
        }),
      );
    },
    [dispatch],
  );

  const loadGeneratedReports = useCallback(
    (payload?: ICustomReportListRequestPayload) => {
      dispatch(
        getGeneratedReports({
          ...payload,
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.GeneratedReports),
        }),
      );
    },
    [dispatch],
  );

  const loadScheduledReports = useCallback(
    (payload?: ICustomReportListRequestPayload) => {
      dispatch(
        getScheduledReports({
          ...payload,
          ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.ScheduledReports),
        }),
      );
    },
    [dispatch],
  );

  const loadTemplates = useCallback(() => {
    dispatch(getTemplates());
  }, [dispatch]);

  const loadTemplateFilters = useCallback(
    async (id: number | undefined) => {
      if (!id) {
        console.error('missing template id');
        return;
      }
      try {
        const template = (await dispatch(loadTemplate({ id })).unwrap()) as ICustomReportTemplate;
        const hydratedTemplate = hydrateTemplate(template, visibleDimensionValues.current);

        setValue('dateRange', hydratedTemplate.dateRange);
        setValue('dateRangeFormat', hydratedTemplate.dateRangeFormat);
        setValue('timeBucket', hydratedTemplate.timeBucket);
        setValue('dimensions', hydratedTemplate.dimensions ?? []);
        setValue('metrics', hydratedTemplate.metrics ?? []);
        setValue('clients', hydratedTemplate.clients);
        setValue('advertisers', hydratedTemplate.advertisers);
        setValue('campaigns', hydratedTemplate.campaigns);
        setValue('events', hydratedTemplate.events ?? []);
        setValue('platforms', hydratedTemplate.platforms ?? []);
      } catch {
        toast.error('Failed to load the template');
      }
    },
    [dispatch, setValue],
  );

  const onSubmitReportFilters = useCallback(async () => {
    setLoadingInstantReportRun(true);
    try {
      await generateReport(getValues(), CUSTOM_REPORT_CHANNEL);
      openNoticeModal();
    } catch (error) {
      toast.error(getReportGenerationErrorMessage(error));
    }
    setLoadingInstantReportRun(false);
    loadGeneratedReports();
  }, [getValues, loadGeneratedReports, openNoticeModal]);
  //#endregion

  //#region useEffects
  useEffect(() => {
    loadTemplates();
    loadGeneratedReports();
  }, [dispatch]);
  //#endregion

  //#region data save
  const reloadTemplateReports = templateReports.reloadTemplates;
  const onCreateNewTemplate = useCallback(
    async (templateName: string) => {
      const newTemplate = await createTemplate(templateName, getValues(), CUSTOM_REPORT_CHANNEL);
      dispatch(appendTemplate(newTemplate));
      reloadTemplateReports();
    },
    [dispatch, getValues, reloadTemplateReports],
  );

  const onScheduleNewReport = useCallback(
    async (scheduleFormData: IScheduleFormData) => {
      const reportFormData = getValues();
      await scheduleReport(scheduleFormData, reportFormData, CUSTOM_REPORT_CHANNEL);
      loadScheduledReports();
      return;
    },
    [getValues, loadScheduledReports],
  );

  const onCancelScheduledReport = useCallback(
    async (id?: number) => {
      try {
        await cancelScheduledReport(id, CUSTOM_REPORT_CHANNEL);
      } catch {
        toast.error('Failed to cancel scheduled report.');
      }
      loadScheduledReports();
    },
    [loadScheduledReports],
  );

  const onEditScheduledReport = useCallback(
    (report: IScheduledCustomReport) => {
      scheduledReportEditingState.open(report);
    },
    [scheduledReportEditingState],
  );

  const onUpdateScheduledReport = useCallback(
    async (scheduleFormData: IScheduleFormData) => {
      const editingReport = scheduledReportEditingState.editingReport;
      if (!editingReport) return;
      await updateScheduledReport(editingReport.id, scheduleFormData, CUSTOM_REPORT_CHANNEL);
      loadScheduledReports();
    },
    [scheduledReportEditingState.editingReport, loadScheduledReports],
  );

  const onReportDownload = useCallback<CustomReportDownloadFunction>(
    id => downloadReport(id, CUSTOM_REPORT_CHANNEL),
    [],
  );
  //#endregion

  const templateNames = useMemo(() => {
    return templates.map(({ name }) => name);
  }, [templates]);

  const [dimensions, metrics, timeBucket] = useWatch({ control, name: ['dimensions', 'metrics', 'timeBucket'] });
  const dateRangeFormat = useWatch({ control, name: 'dateRangeFormat' }) as ScheduledReportFilterDate;
  const platforms = useWatch({ control, name: 'platforms' });

  const previewColumns = useMemo(() => [...(dimensions ?? []), ...(metrics ?? [])], [dimensions, metrics]);

  const { overlappingMetrics, overlappingDimensions } = useMemo(() => {
    const dimensionsOptionsMap = getDimensionsOptionsMap(isSuperAdmin);
    return {
      overlappingDimensions: getOverlappingColumns(
        ...(platforms?.map(platform => dimensionsOptionsMap[platform.value]) ?? []),
      ),
      overlappingMetrics: getOverlappingColumns(
        ...(platforms?.map(platform => METRICS_OPTIONS_MAP[platform.value]) ?? []),
      ),
    };
  }, [platforms, isSuperAdmin]);

  useEffect(() => {
    visibleDimensionValues.current = overlappingDimensions.map(option => option.value);
  }, [overlappingDimensions]);

  const columns = useMemo(
    () => [...overlappingDimensions, ...overlappingMetrics],
    [overlappingMetrics, overlappingDimensions],
  );

  return (
    <>
      <LoadingBackdropWrapper backdropOpen={loadingInstantReportRun}>
        <FormProvider {...filterFormMethods}>
          <Filter
            clients={clients}
            campaigns={campaigns}
            events={events}
            loadingClients={clientsLoading}
            loadingEvents={loadingEvents}
            loadingCampaigns={loadingCampaigns}
            templates={templates}
            loadingTemplates={loadingTemplates}
            loadCampaigns={loadCampaigns}
            loadEvents={loadEvents}
            onSubmit={onSubmitReportFilters}
            onScheduleReportClick={scheduleModalState.onOpen}
            onSaveAsTemplateClick={templateModalState.onOpen}
            loadTemplateFilters={loadTemplateFilters}
            dimensionOptions={overlappingDimensions}
            metricOptions={overlappingMetrics}
            checkColumnExclusion
            usePlatform
            showEventFilter
          />
        </FormProvider>
        <Paper className='grid grid-cols-1 mt-8'>
          <ReportPreviewGrid visibleColumns={previewColumns} columns={columns} timeBucket={timeBucket} />
        </Paper>
        <Paper className='grid grid-cols-1 mt-8'>
          <GeneratedReportsGrid
            data={generatedReports}
            loading={loadingGeneratedReports}
            loadGeneratedReports={loadGeneratedReports}
            rowCount={generatedReportsTotal}
            onReportDownload={onReportDownload}
          />
        </Paper>
        <Paper className='grid grid-cols-1 mt-8'>
          <ScheduledReportsGrid
            data={scheduledReports}
            loading={loadingScheduledReports}
            onCancelScheduledReport={onCancelScheduledReport}
            onEditScheduledReport={onEditScheduledReport}
            loadScheduledReports={loadScheduledReports}
            rowCount={scheduledReportsTotal}
          />
        </Paper>
        <Paper className='grid grid-cols-1 mt-8'>
          <TemplateReportsGrid
            data={templateReports.templates}
            loading={templateReports.loading}
            onShareTemplate={templateReports.onShareTemplate}
            onDeleteTemplate={templateReports.onDeleteTemplate}
          />
        </Paper>
      </LoadingBackdropWrapper>
      <ScheduleReportModal
        onClose={
          scheduledReportEditingState.editingReport ? scheduledReportEditingState.close : scheduleModalState.onClose
        }
        open={scheduleModalState.open || !!scheduledReportEditingState.editingReport}
        mode={scheduledReportEditingState.editingReport ? 'edit' : 'create'}
        initialValues={
          scheduledReportEditingState.editingReport
            ? toScheduleFormDefaults(scheduledReportEditingState.editingReport)
            : undefined
        }
        onSubmit={scheduledReportEditingState.editingReport ? onUpdateScheduledReport : onScheduleNewReport}
        dateRangeFormat={dateRangeFormat}
      />
      <SaveAsTemplateModal
        open={templateModalState.open}
        onClose={templateModalState.onClose}
        onSubmit={onCreateNewTemplate}
        templateNames={templateNames}
      />
      <ShareModal
        open={templateReports.shareTargetId !== null}
        targetTable={SharesTargets.CUSTOM_REPORT_TEMPLATES}
        targetId={templateReports.shareTargetId ?? 0}
        title='Share Template'
        shareWithOrgHelperText='*This template will be shared with all of your team members'
        onModalClose={templateReports.onShareModalClose}
        onSubmit={templateReports.onSubmitShare}
      />
    </>
  );
};

export default OmniplatformReportBuilder;
